/**
 * MechaGuard - Enemy (敌人基类)
 *
 * 职责：
 *   - 沿预定义折线（waypoints）行进，支持多段位移不丢帧
 *   - 生命值 / 护甲 / 回血 / 减速 / 冻结 状态
 *   - 多阶段变身（BOSS）：生命比例跌破阈值时切换形态
 *   - 自身绘制（机体形状 + 血条 + 状态特效）
 *
 * 护甲模型：armor 为 0~1 的减伤比例（0.3 = 减少 30% 伤害）。
 * takeDamage 的 ignoreArmor 表示「无视护甲的比例」，例如 0.4 表示
 * 只生效 60% 的护甲值。
 */

/** 护甲上限，避免多阶段叠加后接近免疫 */
const MAX_ARMOR = 0.85;

let nextEnemyId = 1;

export class Enemy {
  /**
   * @param {object} opts
   * @param {string} opts.typeId  敌人类型标识
   * @param {object} opts.config  类型配置（见 enemy-types/index.js）
   * @param {{x:number,y:number}[]} opts.waypoints 行进折线（世界坐标）
   * @param {number} [opts.hpScale=1] 生命值成长倍率
   * @param {number} [opts.speedMul=1] 速度倍率（关卡难度）
   * @param {(enemy:Enemy, phase:object)=>void} [opts.onPhaseChange] 变身回调
   */
  constructor({ typeId, config, waypoints, hpScale = 1, speedMul = 1, onPhaseChange = null }) {
    this.id = nextEnemyId++;
    this.typeId = typeId;
    this.name = config.name;

    // 位置（世界坐标）
    const start = waypoints[0];
    this.x = start.x;
    this.y = start.y;

    this.waypoints = waypoints;
    this.pathIndex = 1;          // 下一个目标点索引
    this.pathProgress = 0;       // 已行进距离（用于「最前」目标选择）

    // 属性
    this.maxHp = Math.round(config.hp * hpScale);
    this.hp = this.maxHp;

    /** 未计入阶段加成的基准速度 */
    this.baseSpeedRaw = config.speed * speedMul;
    /** 当前有效基准速度（阶段变身会覆盖） */
    this.baseSpeed = this.baseSpeedRaw;

    /** 未计入阶段加成的基准护甲 */
    this.armorBase = config.armor || 0;
    this.armor = this.armorBase;

    this.reward = config.reward;
    this.radius = config.radius;
    this.color = config.color;
    this.shape = config.shape || 'square';
    this.regen = config.regen || 0;

    // 多阶段变身
    /** @type {object[]|null} */
    this.phases = config.phases || null;
    this.phaseIndex = 0;
    /** @type {object|null} */
    this.phase = null;
    this.onPhaseChange = onPhaseChange;
    this.phaseFlash = 0;

    // 状态
    this.alive = true;
    this.reachedBase = false;
    this.slowFactor = 1;
    this.slowTimer = 0;
    this.freezeTimer = 0;
    this.hitFlash = 0;
    this.age = 0;
  }

  /** 当前实际移动速度（像素/秒） */
  get speed() {
    if (this.freezeTimer > 0) return 0;
    return this.baseSpeed * this.slowFactor;
  }

  /** 生命值百分比 0~1 */
  get hpRatio() {
    return this.maxHp > 0 ? Math.max(0, this.hp / this.maxHp) : 0;
  }

  /** 是否是多阶段单位（用于 UI 提示） */
  get hasPhases() {
    return !!(this.phases && this.phases.length);
  }

  /* ===================================================
   * 多阶段变身
   * =================================================== */

  /**
   * 检查生命比例是否跌破下一个阶段阈值。
   * 用 while 而不是 if：一次超高伤害可能连跨多个阶段。
   */
  _checkPhase() {
    if (!this.hasPhases) return;

    while (
      this.phaseIndex < this.phases.length &&
      this.hpRatio <= this.phases[this.phaseIndex].at
    ) {
      const phase = this.phases[this.phaseIndex];
      this.phaseIndex += 1;
      this.phase = phase;

      // 速度按基准值的乘数重算，避免多次叠加
      this.baseSpeed = this.baseSpeedRaw * (phase.speedMul || 1);

      // 护甲为「基准 + 加成」，有上限
      this.armor = Math.min(MAX_ARMOR, this.armorBase + (phase.armorBonus || 0));

      if (phase.regen) this.regen = phase.regen;
      if (phase.color) this.color = phase.color;

      this.phaseFlash = 0.7;

      if (this.onPhaseChange) this.onPhaseChange(this, phase);
    }
  }

  /* ===================================================
   * 更新
   * =================================================== */

  update(dt) {
    if (!this.alive) return;

    this.age += dt;

    // 受击闪白衰减
    if (this.hitFlash > 0) this.hitFlash = Math.max(0, this.hitFlash - dt);

    // 变身闪光衰减
    if (this.phaseFlash > 0) this.phaseFlash = Math.max(0, this.phaseFlash - dt);

    // 多阶段变身检查（伤害是在上一帧的塔/投射物阶段结算的）
    this._checkPhase();

    // 减速恢复
    if (this.slowTimer > 0) {
      this.slowTimer = Math.max(0, this.slowTimer - dt);
      if (this.slowTimer === 0) this.slowFactor = 1;
    }

    // 冻结计时（冻结只阻止移动，其它状态照常结算）
    if (this.freezeTimer > 0) {
      this.freezeTimer = Math.max(0, this.freezeTimer - dt);
    }

    // 回血（精英怪）
    if (this.regen > 0 && this.hp < this.maxHp) {
      this.hp = Math.min(this.maxHp, this.hp + this.regen * dt);
    }

    // 前进（speed 在冻结状态下为 0）
    const speed = this.speed;
    if (speed > 0) this._advance(speed * dt);
  }

  /**
   * 沿折线前进指定距离（跨拐点时不丢失剩余位移）
   * @param {number} distance 本帧可移动的距离
   */
  _advance(distance) {
    let remaining = distance;

    while (remaining > 0 && this.pathIndex < this.waypoints.length) {
      const wp = this.waypoints[this.pathIndex];
      const dx = wp.x - this.x;
      const dy = wp.y - this.y;
      const dist = Math.hypot(dx, dy);

      if (dist <= remaining) {
        // 直达拐点，继续消耗剩余位移
        this.x = wp.x;
        this.y = wp.y;
        this.pathProgress += dist;
        remaining -= dist;
        this.pathIndex++;
      } else {
        // 本帧停在两点之间
        this.x += (dx / dist) * remaining;
        this.y += (dy / dist) * remaining;
        this.pathProgress += remaining;
        remaining = 0;
      }
    }

    if (this.pathIndex >= this.waypoints.length) {
      this.reachedBase = true;
    }
  }

  /* ===================================================
   * 伤害与状态
   * =================================================== */

  /**
   * 承受伤害
   * @param {number} amount 原始伤害
   * @param {{ignoreArmor?:number}} [opts] ignoreArmor 为无视护甲的比例 0~1
   * @returns {number} 实际造成的伤害
   */
  takeDamage(amount, opts = {}) {
    if (!this.alive) return 0;

    const ignore = Math.min(1, Math.max(0, opts.ignoreArmor || 0));
    const effectiveArmor = this.armor * (1 - ignore);
    const dealt = Math.max(1, amount * (1 - effectiveArmor));

    this.hp -= dealt;
    this.hitFlash = 0.12;

    if (this.hp <= 0) {
      this.hp = 0;
      this.alive = false;
    }

    return dealt;
  }

  /**
   * 施加/刷新减速
   * @param {number} factor 速度倍率（0.7 = 降低 30% 速度）
   * @param {number} duration 持续时间（秒）
   */
  applySlow(factor, duration) {
    if (!this.alive) return;
    // 取更强的减速，并刷新时间
    this.slowFactor = Math.min(this.slowFactor === 1 ? factor : this.slowFactor, factor);
    this.slowTimer = Math.max(this.slowTimer, duration);
  }

  /** 施加冻结 */
  applyFreeze(duration) {
    if (!this.alive) return;
    this.freezeTimer = Math.max(this.freezeTimer, duration);
  }

  /** 与某点的距离平方（避免开方） */
  distanceSqTo(x, y) {
    const dx = this.x - x;
    const dy = this.y - y;
    return dx * dx + dy * dy;
  }

  /** 是否处于减速状态 */
  get isSlowed() {
    return this.slowFactor < 1 && this.slowTimer > 0;
  }

  /* ===================================================
   * 绘制（世界坐标，相机变换已由调用方施加）
   * =================================================== */

  draw(ctx) {
    if (!this.alive) return;

    const r = this.radius;

    ctx.save();
    ctx.translate(this.x, this.y);

    // 机体发光
    ctx.shadowColor = this.color;
    ctx.shadowBlur = this.hitFlash > 0 ? 26 : 14;

    ctx.fillStyle = this.hitFlash > 0 ? '#ffffff' : this.color;
    ctx.strokeStyle = 'rgba(255, 255, 255, 0.85)';
    ctx.lineWidth = 1.5;

    this._traceShape(ctx, r);
    ctx.fill();
    ctx.stroke();

    // 内层暗芯，增加层次
    ctx.shadowBlur = 0;
    ctx.fillStyle = 'rgba(5, 12, 24, 0.55)';
    this._traceShape(ctx, r * 0.48);
    ctx.fill();

    ctx.restore();

    // 状态特效
    this._drawStatus(ctx);

    // 血条
    this._drawHealthBar(ctx);
  }

  _traceShape(ctx, r) {
    switch (this.shape) {
      case 'triangle':
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.lineTo(r * 0.9, r * 0.75);
        ctx.lineTo(-r * 0.9, r * 0.75);
        ctx.closePath();
        break;

      case 'diamond':
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.lineTo(r, 0);
        ctx.lineTo(0, r);
        ctx.lineTo(-r, 0);
        ctx.closePath();
        break;

      case 'hexagon':
        ctx.beginPath();
        for (let i = 0; i < 6; i++) {
          const a = (Math.PI / 3) * i;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;

      case 'octagon':
        ctx.beginPath();
        for (let i = 0; i < 8; i++) {
          const a = (Math.PI / 4) * i + Math.PI / 8;
          const px = Math.cos(a) * r;
          const py = Math.sin(a) * r;
          if (i === 0) ctx.moveTo(px, py);
          else ctx.lineTo(px, py);
        }
        ctx.closePath();
        break;

      case 'square':
      default:
        ctx.beginPath();
        ctx.rect(-r * 0.8, -r * 0.8, r * 1.6, r * 1.6);
        break;
    }
  }

  _drawStatus(ctx) {
    // 多阶段变身：旋转的危险光环（阶段越高越大越亮）
    if (this.phase) {
      const pulse = 1 + Math.sin(this.age * 6) * 0.06;
      const extra = this.phaseFlash > 0 ? this.phaseFlash * 12 : 0;

      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.rotate(this.age * (0.6 + this.phaseIndex * 0.5));

      ctx.strokeStyle = this.color;
      ctx.globalAlpha = 0.75;
      ctx.lineWidth = 2 + this.phaseIndex * 0.6;
      ctx.setLineDash([7, 7]);

      const rr = (this.radius + 8 + this.phaseIndex * 2 + extra) * pulse;
      ctx.beginPath();
      ctx.arc(0, 0, rr, 0, Math.PI * 2);
      ctx.stroke();

      ctx.restore();
    }

    // 减速：冰蓝外环
    if (this.isSlowed) {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.strokeStyle = 'rgba(120, 220, 255, 0.9)';
      ctx.lineWidth = 2;
      ctx.setLineDash([4, 4]);
      ctx.beginPath();
      ctx.arc(0, 0, this.radius + 5, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }

    // 冻结：实心冰壳
    if (this.freezeTimer > 0) {
      ctx.save();
      ctx.translate(this.x, this.y);
      ctx.fillStyle = 'rgba(180, 240, 255, 0.35)';
      ctx.beginPath();
      ctx.arc(0, 0, this.radius + 3, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(220, 250, 255, 0.95)';
      ctx.lineWidth = 2;
      ctx.stroke();
      ctx.restore();
    }
  }

  _drawHealthBar(ctx) {
    const w = Math.max(24, this.radius * 2.4);
    const h = 4;
    const x = this.x - w / 2;
    const y = this.y - this.radius - 11;

    const ratio = this.hpRatio;

    // 背景
    ctx.fillStyle = 'rgba(0, 0, 0, 0.55)';
    ctx.fillRect(x - 1, y - 1, w + 2, h + 2);

    // 血量颜色：绿 -> 黄 -> 红
    let color = '#00ff88';
    if (ratio < 0.6) color = '#ffcc00';
    if (ratio < 0.3) color = '#ff3366';

    ctx.fillStyle = color;
    ctx.fillRect(x, y, w * ratio, h);

    // 护甲标识
    if (this.armor > 0) {
      ctx.strokeStyle = 'rgba(160, 200, 255, 0.85)';
      ctx.lineWidth = 1;
      ctx.strokeRect(x - 1.5, y - 1.5, w + 3, h + 3);
    }

    // 阶段名（BOSS 专属）
    if (this.phase) {
      ctx.save();
      ctx.font = 'bold 10px system-ui, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'bottom';

      ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
      ctx.fillText(this.phase.name, this.x + 1, y - 4);

      ctx.fillStyle = this.color;
      ctx.fillText(this.phase.name, this.x, y - 5);
      ctx.restore();
    }
  }
}
